import React, { PureComponent } from 'react';

import {
  SimpleForm,
  SimpleShowLayout,
  SelectInput,
  TextInput,
  ImageInput,
  FormDataConsumer,
  required,
  BooleanInput,
  useNotify,
  useRefresh,
} from 'react-admin';
import { withStyles } from '@mui/styles';
import { useFormContext } from 'react-hook-form';
import { equipmentSelectableOptions } from '../../../constants/equipment';
import { difficultySelectableOptions } from '../../../constants/difficulty';
import { programCategoryOptions } from '../../../constants/category';
import ImgField from '../../../components/ImgField/ImgField';
import ImagePickerButton from '../../../components/ImagePickerButton/ImagePickerButton';
import uploadPicture from '../../../utils/uploadPicture';
import ListField from './ListField';
import IntroScreensComponent from './IntroScreens';
import StoriesInput from './StoriesInput';
import dataProvider from '../../../providers/dataProvider';
import ToolBarEditProgram from './ToolbarEditProgram';
import Loading from '../../../components/Loading/Loading';
import Sessions from '../../Sessions/Sessions/Sessions';
import PublishProgram from './PublishProgram';

// Action types for dataProvider calls
const CREATE = 'CREATE';
const UPDATE = 'UPDATE';
const DELETE = 'DELETE';

// Helper component to provide setValue to FormDataConsumer children
const FormDataConsumerWithSetValue = ({ children }) => {
  const formContext = useFormContext();
  // Guard against null form context during initial render
  if (!formContext) {
    return null;
  }
  const { setValue } = formContext;
  return <FormDataConsumer>{(props) => children({ ...props, setValue })}</FormDataConsumer>;
};

const styles = {
  simpleShowLayout: {
    width: '100%',
    marginBottom: '20px',
    background: 'white',
    boxShadow:
      '0px 1px 3px 0px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.12)',
    '& .MuiFormControl-root': {
      width: '100%',
    },
  },
  simpleShowLayoutTitle: {
    paddingLeft: '0',
  },
  longerInput: {
    width: '50%',
  },
  input: {
    width: '100%',
  },
  inlineBlock: { display: 'inline-flex', marginRight: '1rem' },
  publish: {
    float: 'right',
  },
};

class EditFormProgram extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      videoUploading: false,
    };
  }

  handleVideoUploadingChange = (videoUploading) => {
    this.setState({ videoUploading });
  };

  uploadImg = (img) => uploadPicture(img);

  setUpStoriesToProgram = (stories, programId) => {
    if (!stories || !programId) {
      return Promise.resolve(null);
    }
    const requests = stories
      .map((res) => {
        if (res.id && res.text && res.author) {
          return dataProvider(UPDATE, `programs/${programId}/stories`, {
            id: res.id,
            data: res,
          });
        }
        if (!res.id && res.text) {
          return dataProvider(CREATE, `programs/${programId}/stories`, { data: res });
        }
        if (res.id && !res.text && !res.author) {
          return dataProvider(DELETE, `programs/${programId}/stories`, {
            id: res.id,
            data: res,
          });
        }
      })
      .filter((res) => res);
    return Promise.all(requests);
  };

  save = (data) => {
    this.setState({ loading: true });
    // `video` is a dangling top-level field set by IntroScreens' FileInput;
    // intro video data is read from introScreens[1].video instead.
    const { newPicture, stories, primaryProductCandidates, video, ...dataCache } = { ...data };
    dataCache.category = dataCache.category || null;
    dataCache.primaryProductId = dataCache.primaryProductId ?? null;

    // Get programId and original picture from form data (available in v5)
    const programId = data.id;
    const originalPicture = data.picture;
    const promiseArray = [this.uploadImg(newPicture)];
    if (dataCache.isExternal) {
      dataCache.complexity = 0;
      dataCache.equipment = 0;

      delete dataCache.allowedTags;
      delete dataCache.bullets;
      delete dataCache.forbiddenTags;
      delete dataCache.introScreens;
      delete dataCache.isDefault;
      delete dataCache.isPublic;
      delete dataCache.sessions;
      delete dataCache.storiesUrl;
    } else {
      delete dataCache.externalLink;
      delete dataCache.isAlphaPosse;

      promiseArray.push(this.setUpStoriesToProgram(stories, programId));
    }

    Promise.all(promiseArray)
      .then((res) => {
        // Use uploaded picture, or fall back to original if no new picture was uploaded
        dataCache.picture = res[0].data.fileName || originalPicture;
        if (
          !dataCache.isExternal &&
          dataCache.introScreens &&
          dataCache.introScreens[1] &&
          dataCache.introScreens[1].video &&
          dataCache.introScreens[1].video.id
        ) {
          // Video was uploaded on drop — strip the video object and keep just the id
          const { video, ...newintroScreens1 } = dataCache.introScreens[1];
          dataCache.introScreens[1] = { ...newintroScreens1, videoId: video.id };
        }
        if (!dataCache.isExternal) {
          dataCache.storiesUrl = dataCache.storiesUrl ? dataCache.storiesUrl : null;
        }

        // Use dataProvider directly for react-admin v5
        return dataProvider.update('programs', { id: programId, data: dataCache });
      })
      .then(() => {
        this.props.notify('Program saved', { type: 'success' });
        this.props.refresh();
      })
      .catch((error) => {
        this.props.notify(error?.message || 'Failed to save program', { type: 'error' });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  handlePublic = (formData) => {
    const nextIsPublic = !formData.isPublic;
    this.setState({ loading: true });
    dataProvider('UPDATE', 'programs', {
      id: formData.id,
      data: { isPublic: nextIsPublic },
    })
      .then(() => {
        this.props.notify(nextIsPublic ? 'Program published' : 'Program hidden', {
          type: 'success',
        });
        this.props.refresh();
      })
      .catch((error) => {
        this.props.notify(error?.message || 'Failed to update program visibility', {
          type: 'error',
        });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  getDefault = () => {
    const record = this.props.record || {};
    // Format newPicture as an object with src for ImageInput to display
    const newPicture = record.picture
      ? {
          picture: record.picture,
          src: `${process.env.REACT_APP_IMAGES_FILE_HOST}/fit-in/100x100/${record.picture}`,
        }
      : null;
    return {
      ...record,
      newPicture,
    };
  };

  renderPublishButton = (formData) => {
    const { record } = this.props;
    if (!record) {
      return null;
    }

    if (record.isExternal) {
      return (
        <PublishProgram
          formData={formData}
          handlePublic={this.handlePublic}
          isReady={!!record.externalLink}
        />
      );
    }

    return (
      record.sessions && (
        <PublishProgram
          formData={formData}
          handlePublic={this.handlePublic}
          isReady={!!record.sessions.length}
        />
      )
    );
  };

  render() {
    const { classes, record, permissions, notify, refresh, ...props } = this.props;
    return (
      <>
        <Loading open={this.state.loading} />
        <SimpleForm
          {...props}
          onSubmit={this.save}
          record={record}
          defaultValues={this.getDefault}
          toolbar={<ToolBarEditProgram uploading={this.state.videoUploading} />}
        >
          <SimpleShowLayout className={classes.simpleShowLayout}>
            <FormDataConsumer>
              {({ formData }) => this.renderPublishButton(formData)}
            </FormDataConsumer>
            <TextInput source="name" label="Program Name" validate={required()} />
            <SelectInput
              source="category"
              label="Category"
              choices={programCategoryOptions}
              emptyText="— none —"
            />
            <BooleanInput source="isExternal" label="Is External" />
            <FormDataConsumer>
              {({ formData, ...rest }) => {
                if (formData.isExternal) {
                  return (
                    <>
                      <BooleanInput
                        source="isAlphaPosse"
                        label="Is Alpha Posse (allow add/remove access to discourse for this program)"
                        {...rest}
                      />
                      <br />
                      <TextInput
                        source="externalLink"
                        label="External Link"
                        validate={required()}
                        {...rest}
                      />
                    </>
                  );
                }

                return (
                  <>
                    <SelectInput
                      label="Equipment"
                      source="equipment"
                      choices={equipmentSelectableOptions}
                      validate={required()}
                      {...rest}
                    />
                    <br />
                    <SelectInput
                      label="Type"
                      source="complexity"
                      choices={difficultySelectableOptions}
                      validate={required()}
                      {...rest}
                    />
                  </>
                );
              }}
            </FormDataConsumer>
            <TextInput source="description" label="Description" validate={required()} />
            <TextInput
              source="howTo"
              label="How To Use (HTML or plain text)"
              multiline
              rows={6}
              fullWidth
            />
            <ImageInput
              className={classes.inlineBlock}
              source="newPicture"
              label="Add Program Photo"
              accept={{ 'image/*': [] }}
              validate={required()}
            >
              <ImgField width="100" height="100" />
            </ImageInput>
            <ImagePickerButton source="newPicture" label={false} />
          </SimpleShowLayout>
          <FormDataConsumer>
            {({ formData }) => {
              if (!formData?.isExternal) {
                return (
                  <SimpleShowLayout className={classes.simpleShowLayoutTitle}>
                    <h2>Buy Program</h2>
                  </SimpleShowLayout>
                );
              }
              return null;
            }}
          </FormDataConsumer>
          <FormDataConsumer>
            {({ formData, ...rest }) => {
              if (formData?.isExternal) return null;
              const candidates = formData?.primaryProductCandidates;
              if (!candidates?.length) {
                return (
                  <SimpleShowLayout className={classes.simpleShowLayout}>
                    <p>
                      No single-program products grant this program yet — no Primary Product can be
                      chosen. Programs without a Primary Product appear locked on the PWA library
                      and are not surfaced as LEARN MORE cards.
                    </p>
                  </SimpleShowLayout>
                );
              }
              return (
                <SimpleShowLayout className={classes.simpleShowLayout}>
                  <SelectInput
                    source="primaryProductId"
                    label="Primary Product"
                    helperText="Drives the LEARN MORE card on the PWA library"
                    choices={candidates}
                    emptyText="— none —"
                    {...rest}
                  />
                </SimpleShowLayout>
              );
            }}
          </FormDataConsumer>
          <FormDataConsumerWithSetValue>
            {({ formData, setValue }) => {
              if (!formData?.isExternal) {
                return (
                  <SimpleShowLayout className={classes.simpleShowLayout}>
                    <h4>Bullet Points</h4>
                    {formData?.bullets && (
                      <ListField
                        source="bullets"
                        value={formData.bullets}
                        fieldsNumber={4}
                        handleData={(value) => setValue('bullets', value)}
                      />
                    )}
                  </SimpleShowLayout>
                );
              }
              return null;
            }}
          </FormDataConsumerWithSetValue>
          <FormDataConsumerWithSetValue>
            {({ formData, setValue }) => {
              if (!formData?.isExternal) {
                return (
                  <SimpleShowLayout className={classes.simpleShowLayout}>
                    <h4>GMB Stories</h4>
                    <TextInput source="storiesUrl" label="URL" />
                    {formData?.id && (
                      <StoriesInput
                        source="stories"
                        value={formData}
                        fieldsNumber={4}
                        handleData={(value) => setValue('stories', value)}
                      />
                    )}
                  </SimpleShowLayout>
                );
              }
              return null;
            }}
          </FormDataConsumerWithSetValue>
          <FormDataConsumer>
            {({ formData }) => {
              if (!formData?.isExternal) {
                return (
                  <SimpleShowLayout className={classes.simpleShowLayoutTitle}>
                    <h2>Build Program</h2>
                  </SimpleShowLayout>
                );
              }
              return null;
            }}
          </FormDataConsumer>
          <FormDataConsumerWithSetValue>
            {({ formData, setValue }) => {
              if (!formData?.isExternal) {
                return (
                  <SimpleShowLayout className={classes.simpleShowLayout}>
                    <h4>Introduction Screens</h4>
                    {formData?.introScreens && (
                      <IntroScreensComponent
                        value={formData}
                        source="introScreens"
                        handleData={(value) => setValue('introScreens', value)}
                        onUploadingChange={this.handleVideoUploadingChange}
                      />
                    )}
                  </SimpleShowLayout>
                );
              }
              return null;
            }}
          </FormDataConsumerWithSetValue>
          <FormDataConsumer>
            {({ formData }) => {
              if (!formData?.isExternal) {
                return (
                  <SimpleShowLayout className={classes.simpleShowLayout}>
                    <Sessions source="sessions" values={record} permissions={permissions} />
                  </SimpleShowLayout>
                );
              }
              return null;
            }}
          </FormDataConsumer>
        </SimpleForm>
      </>
    );
  }
}

const EditFormProgramWithNavigation = (props) => {
  const notify = useNotify();
  const refresh = useRefresh();
  return <EditFormProgram {...props} notify={notify} refresh={refresh} />;
};

export default withStyles(styles)(EditFormProgramWithNavigation);
